import React, { Fragment, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import isEmpty from 'lodash/isEmpty';
import messages from 'aurora-translations';
import { ADGMPagination } from 'aurora-components';
import Summary from './Summary';
import styles from './styles';

function ContractList(props) {
  const { classes, locale, loading, data, query, page, handleQueryChange, ...otherProps } = props;

  const isArabic = useMemo(() => locale === 'ar', [locale]);
  const noData =
    isEmpty(data?.responseDTO) &&
    isEmpty(data?.offplanContracts) &&
    isEmpty(data?.musatahaContracts) &&
    isEmpty(data?.contracts);

  const contracts = useMemo(() => {
    if (noData) return [];
    if (otherProps.type === 'Offplan') {
      return data?.offplanContracts?.map((prop, index) => ({
        id: index + 1,
        buyerName: isArabic ? prop.buyerNameAr : prop.buyerNameEn,
        projectName: isArabic ? prop.projectNameAr : prop.projectNameEn,
        buildingName: isArabic ? prop.buildingNameAr : prop.buildingNameEn,
        contractStatus: isArabic ? prop.contractStatusAr : prop.contractStatusEn,
        developerName: isArabic ? prop.developerNameAr : prop.developerNameEn,
        ...prop,
      }));
    }
    if (otherProps.type === 'Musataha') {
      return data?.musatahaContracts?.map((prop, index) => ({
        id: index + 1,
        name: isArabic ? prop.nameAr : prop.nameEn,
        contractStatus: isArabic ? prop.contractStatusAr : prop.contractStatusEn,
        ...prop,
      }));
    }
    if (otherProps.type === 'Mortgage') {
      return data?.contracts?.map((prop, index) => ({
        id: index + 1,
        amount: prop?.mortgageValue,
        contractId: prop?.mortgageId,
        contractNumber: prop?.mortgageAgreementNumber,
        startDate: prop?.contractStartDate,
        endDate: prop?.contractEndDate,
        mortgageType: prop?.[isArabic ? 'mortgageTypeNameA' : 'mortgageTypeNameE'],
        mortgageBank: prop?.[isArabic ? 'bankNameA' : 'bankNameE'],
        ...prop,
      }));
    }
    if (otherProps.type === 'Poa') {
      return data?.responseDTO?.map((prop, index) => ({
        id: index + 1,
        secondPartyName: isArabic ? prop.secondPartyNameAr : prop.secondPartyNameEn,
        contractType: isArabic ? prop.contractTypeAr : prop.contractTypeEn,
        ...prop,
      }));
    }
    return data?.responseDTO?.map((prop, index) => ({
      id: index + 1,
      tenantName: isArabic ? prop.tenantNameAr : prop.tenantNameEn,
      firstPartyName: isArabic ? prop.firstPartyNameAr : prop.firstPartyNameEn,
      secondPartyName: isArabic ? prop.secondPartyNameAr : prop.secondPartyNameEn,
      ...prop,
    }));
  }, [data, isArabic]);

  const handlePageChange = useCallback(
    num => {
      const newQuery = { ...query, page: num };
      handleQueryChange(newQuery);
    },
    [query],
  );

  return (
    <div className={classes.contractListContainer}>
      {!loading && noData && (
        <div className={classes.noDataContainer}>
          <Typography component='p'>
            <FormattedMessage {...messages.error_930040} />
          </Typography>
        </div>
      )}
      {!isEmpty(contracts) && (
        <Fragment>
          <List className={classes.contractList}>
            {contracts.map(cont => (
              <Summary key={cont?.contractId} contract={cont} {...otherProps} />
            ))}
          </List>
          <ADGMPagination
            totalPages={data?.totalPages || 0}
            currentPage={Number(page)}
            onPageChange={handlePageChange}
          />
        </Fragment>
      )}
    </div>
  );
}

ContractList.propTypes = {
  classes: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  data: PropTypes.object,
  query: PropTypes.object.isRequired,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handleQueryChange: PropTypes.func.isRequired,
};

ContractList.defaultProps = {
  data: null,
};

export default withStyles(styles)(ContractList);
